import React, { useState, useRef, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

// A slider for the marketing showcase sections.
//
// All slides are rendered into a translated track rather than swapping the active one in
// and out, so every slide's content is present in the prerendered HTML that react-snap
// produces. Crawlers see the whole showcase, not just slide one.
//
// `slides` is an array of { key, node, label }.
function ShowcaseSlider({ d, slides, ariaLabel = "Product showcase" }) {
    const [index, setIndex] = useState(0);
    const touchStartX = useRef(null);
    const touchEndX = useRef(null);

    const count = slides.length;
    const go = useCallback((next) => setIndex(((next % count) + count) % count), [count]);

    const onTouchStart = (e) => {
        touchEndX.current = null;
        touchStartX.current = e.targetTouches[0].clientX;
    };
    const onTouchMove = (e) => {
        touchEndX.current = e.targetTouches[0].clientX;
    };
    const onTouchEnd = () => {
        if (touchStartX.current === null || touchEndX.current === null) return;
        const distance = touchStartX.current - touchEndX.current;
        if (Math.abs(distance) < 50) return;
        go(distance > 0 ? index + 1 : index - 1);
    };

    const onKeyDown = (e) => {
        if (e.key === "ArrowRight") {
            e.preventDefault();
            go(index + 1);
        } else if (e.key === "ArrowLeft") {
            e.preventDefault();
            go(index - 1);
        }
    };

    if (count === 0) return null;

    const navBtn = `w-10 h-10 rounded-full border flex items-center justify-center transition-colors ${
        d
            ? "border-white/[0.12] text-gray-300 hover:bg-white/[0.06] hover:text-white"
            : "border-gray-300 text-gray-600 hover:bg-gray-100 hover:text-gray-900"
    }`;

    return (
        <div
            role="group"
            aria-roledescription="carousel"
            aria-label={ariaLabel}
            tabIndex={0}
            onKeyDown={onKeyDown}
            className="focus:outline-none"
        >
            <div
                className={`rounded-[20px] border overflow-hidden ${d ? "border-white/[0.08]" : "border-gray-200 shadow-sm"}`}
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
            >
                <div
                    className="flex transition-transform duration-500 ease-out"
                    style={{ transform: `translateX(-${index * 100}%)` }}
                >
                    {slides.map((slide, i) => (
                        <div
                            key={slide.key}
                            className="w-full flex-shrink-0"
                            aria-hidden={i !== index}
                            role="group"
                            aria-roledescription="slide"
                            aria-label={`${i + 1} of ${count}${slide.label ? `: ${slide.label}` : ""}`}
                        >
                            {slide.node}
                        </div>
                    ))}
                </div>
            </div>

            {count > 1 && (
                <div className="flex items-center justify-between mt-5">
                    <button onClick={() => go(index - 1)} className={navBtn} aria-label="Previous slide">
                        <ChevronLeft className="w-4 h-4" />
                    </button>

                    <div className="flex items-center gap-2">
                        {slides.map((slide, i) => (
                            <button
                                key={slide.key}
                                onClick={() => go(i)}
                                aria-label={`Go to slide ${i + 1}`}
                                aria-current={i === index}
                                className={`h-2 rounded-full transition-all ${
                                    i === index
                                        ? d
                                            ? "w-6 bg-white"
                                            : "w-6 bg-gray-900"
                                        : d
                                          ? "w-2 bg-white/25 hover:bg-white/40"
                                          : "w-2 bg-gray-300 hover:bg-gray-400"
                                }`}
                            />
                        ))}
                    </div>

                    <button onClick={() => go(index + 1)} className={navBtn} aria-label="Next slide">
                        <ChevronRight className="w-4 h-4" />
                    </button>
                </div>
            )}

            {slides[index]?.label && (
                <p className={`text-center text-sm font-light mt-4 ${d ? "text-gray-500" : "text-gray-500"}`}>
                    {slides[index].label}
                </p>
            )}
        </div>
    );
}

export default ShowcaseSlider;
